import React, { useState } from 'react'
import styled from 'styled-components'
import { APIProvider } from '@vis.gl/react-google-maps'

import Card from 'components/Card'
import { color } from 'styles/colors'

import CustomerInfoRow from './CustomerInfoRow'
import Pagination from 'components/Pagination'
import ActivityMapPanel from './ActivityMapPanel'

import useRecentCustomerActivity from 'hooks/useRecentCustomerActivity'
import useScreenSize from 'hooks/useScreenSize'

import { useSessionContext } from 'hooks/SessionContext'
import { screenSize } from 'styles/shared'

const ITEMS_PER_PAGE = 5

const CustomerActivityCard = () => {
  const { session: { dealerId } } = useSessionContext()
  const screenSizeValue = useScreenSize()
  const isStackedLayout = screenSizeValue !== 'large'
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null)
  const { areRecentCustomerActivityLoaded, recentActivities } = useRecentCustomerActivity(dealerId)
  const totalPages = Math.ceil(recentActivities.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedActivities = recentActivities.slice(startIndex, endIndex)
  const isNoRecentActivity = areRecentCustomerActivityLoaded && recentActivities.length === 0
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setSelectedCustomerId(null)
  }
  const activityImage = isNoRecentActivity ? '/img/hehm-no-recent-activity.svg' : '/img/activity.svg'

  const showPagination = !isNoRecentActivity && totalPages > 1
  const pagination = showPagination ? (
    <S.PaginationContainer>
      <S.Pagination
        size="medium"
        pageCount={totalPages}
        onPageChange={handlePageChange}
        page={currentPage}
      />
    </S.PaginationContainer>
  ) : null

  return (
    <S.StyledCard
      titleAreaLeft={
        areRecentCustomerActivityLoaded && (
          <img
            src={activityImage}
            alt="HEHM Health Icon"
            style={{ marginRight: '15px' }}
          />
        )
      }
      title={CustomerActivityCard.cardTitle}
      titleAreaStyle={S.titleAreaStyle(isNoRecentActivity)}
      isLoaded={areRecentCustomerActivityLoaded}
    >
      <S.Subtitle>
        Including any equipment alerts in the past 24 hours
      </S.Subtitle>
      <S.Content $isNoRecentActivity={isNoRecentActivity}>
        <S.ListSection $isNoRecentActivity={isNoRecentActivity}>
          {isNoRecentActivity ? (
            <S.EmptyState>
              <S.EmptyTitle>No recent activity</S.EmptyTitle>
              <S.EmptyDescription data-testid="empty-state-description">
                There is no recent activity in the past 24 hours to show. This
                section will update as new information is available.
              </S.EmptyDescription>
            </S.EmptyState>
          ) : (
            paginatedActivities.map((deviceActivity) => (
              <CustomerInfoRow
                key={deviceActivity.customerId}
                customer={deviceActivity}
                isSelected={selectedCustomerId === deviceActivity.customerId}
                onSelectCallback={setSelectedCustomerId}
              />
            ))
          )}
        </S.ListSection>

        {isStackedLayout && pagination}

        <S.MapSection $isNoRecentActivity={isNoRecentActivity}>
          <APIProvider apiKey={'AIzaSyAQmXdUbXBcTHqNAcQv93SKjMhgr8HTrxE'}>
            <ActivityMapPanel
              customersWithLocation={paginatedActivities}
              selectedCustomerId={selectedCustomerId}
              onCustomerSelectCallback={setSelectedCustomerId}
            />
          </APIProvider>
        </S.MapSection>
      </S.Content>

      {!isStackedLayout && pagination}
    </S.StyledCard>
  )
}

CustomerActivityCard.cardTitle = 'Activity in the Last 24 Hours'
CustomerActivityCard.cardKey = 'recent_activity'

export default CustomerActivityCard

const S = {
  StyledCard: styled(Card)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 16px;
    border: 1px solid ${color('dashboardCardBorder')};
    font-family: 'Inter', regular;
  `,
  titleAreaStyle: (isNoRecentActivity: boolean) => `
    align-items: center;
    h2 {
      margin-bottom: 0;
      line-height: 1.15;
      font-weight: 600;
      color: ${isNoRecentActivity ? color('inactiveCardTitle') : color('premiumText')};
    }
  `,
  Subtitle: styled.p<{ $isNoRecentActivity?: boolean }>`
    margin: 8px 0 16px;
    color: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? color('inactiveCardText') : color('secondaryText')};
  `,
  Content: styled.div<{ $isNoRecentActivity?: boolean }>`
    display: flex;
    gap: 16px;
    ${({ $isNoRecentActivity }) => $isNoRecentActivity && 'position: relative;'}
    @media ${screenSize.medium} {
      flex-direction: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? 'row' : 'column'};
    }
  `,
  PaginationContainer: styled.div`
    display: flex;
    justify-content: center;
    margin-top: 4px;
  `,
  ListSection: styled.div<{ $isNoRecentActivity?: boolean }>`
    flex: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? '1' : '0 0 50%'};
    min-width: 0;
    ${({ $isNoRecentActivity }) => $isNoRecentActivity && `
      display: flex;
      align-items: flex-start;
      padding-right: 156px;
    `}

    @media ${screenSize.medium} {
      flex: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? '1' : '0 0 100%'};
    }

    @media ${screenSize.small} {
      flex: 0 0 100%;
      padding-right: 0;
    }
  `,
  MapSection: styled.div<{ $isNoRecentActivity?: boolean }>`
    flex: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? '0 0 140px' : '1'};
    border-radius: 12px;
    overflow: hidden;
    ${({ $isNoRecentActivity }) => $isNoRecentActivity && `
      pointer-events: none;
      filter: grayscale(100%);
      position: absolute;
      right: 0;
      bottom: 0;
    `}

    > div {
      height: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? '140px !important' : '100%'};
      width:  ${({ $isNoRecentActivity }) => $isNoRecentActivity ? '140px !important' : '100%'};

      @media ${screenSize.small} {
        display: ${({ $isNoRecentActivity }) => $isNoRecentActivity ? 'none' : 'block'};
      }
    }
  `,
  Pagination: styled(Pagination)`
    align-self: center;
  `,
  EmptyState: styled.div`
    display: flex;
    flex-direction: column;
    align-items: baseline;
    text-align: start;
    font-family: Inter;
    border-radius: 12px;
    width: fit-content;
  `,
  EmptyTitle: styled.p`
    margin: 0 0 8px;
    font-weight: 600;
    color: ${color('inactiveCardTitle')};
    font-size: 24px;

    @media ${screenSize.small} {
      font-size: 18px;
    }
  `,
  EmptyDescription: styled.p`
    color: ${color('inactiveCardText')};
    font-size: 14px;

    @media ${screenSize.small} {
      font-size: 13px;
      max-width: fit-content;
      margin: 10px 0 0;
    }
  `
}
